import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import "./KanbanBoard.scss";
import LoadingScreen from "../LoadingScreen/LoadingScreen";

// Cards are revealed 10 at a time per column. When this component owns its
// data, it fetches subsequent pages from the stage-filtered endpoint. When a
// parent supplies data, that data is authoritative so its filters remain in
// effect on the Kanban as well as the list.
const KANBAN_COLUMN_PAGE_SIZE = 10;

// ── Stage totals from /stage-summary ────────────────────────────────────────
//
// /stage-summary returns one record per current stage:
//   { stageName, totalEnquiries, totalQuotationAmount, histories: [...] }
//
// Returns a Map keyed by stageName → { count, amount }, matching the
// stageName-based column ids used elsewhere in this file.
const buildStageTotalsFromSummary = (summary) => {
  const totals = new Map();
  (summary || []).forEach((stage) => {
    const key = stage.stageName;
    if (!key) return;
    totals.set(key, {
      count: Number(stage.totalEnquiries || 0),
      amount: Number(stage.totalQuotationAmount || 0),
    });
  });
  return totals;
};

const stageSummaryToEnquiries = (summary) => {
  const stages = summary || [];
  const enquiryStages = stages.map((stage, index) => ({
    enquiryStageId: stage.enquiryStageId,
    stageName: stage.stageName,
    sequenceNumber: index + 1,
  }));

  return stages.flatMap((stage) =>
    (stage.histories || []).map((history) => ({
      enquiryId: history.enquiryId,
      enquiryCode: history.enquiryCode,
      totalQuotationAmount: history.totalQuotationAmount || 0,
      enquiryStages,
      stageHistories: [
        {
          enquiryStageId: stage.enquiryStageId,
          stageName: stage.stageName,
          enquiryStatus: "PROCESSING",
        },
      ],
    })),
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeStatus = (s) => (s || "PENDING").toUpperCase();

const isActiveStatus = (s) =>
  ["PROCESSING", "IN_PROGRESS", "IN PROGRESS", "ASSIGNED"].includes(
    normalizeStatus(s),
  );

const isCompletedStatus = (s) =>
  ["COMPLETED", "COMPLETE", "DONE"].includes(normalizeStatus(s));

const isCancelledStatus = (s) =>
  ["CANCELLED", "CANCELED", "CANCEL", "REJECTED", "LOST", "FAILED"].includes(
    normalizeStatus(s),
  );

const fmtMoney = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });

const capitalize = (s) =>
  s
    ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, " ")
    : "—";

// ─── Stage column builders ─────────────────────────────────────────────────────
//
// The API returns two stage config arrays on every enquiry:
//
//   enquiryStages[]   – the enquiry pipeline stages
//     { enquiryStageId, stageName, sequenceNumber, stageType, status }
//
//   operationStages[] – the operations pipeline stages
//     { operationStageId, name, sequenceNumber, status }
//
// We collect these across all enquiries, dedupe by id, sort by sequenceNumber,
// and use them as the Kanban columns.

const buildEnquiryStageColumns = (rawData) => {
  const map = new Map(); // enquiryStageId → stage object
  rawData.forEach((eq) => {
    (eq.enquiryStages || []).forEach((s) => {
      if (s.enquiryStageId && !map.has(s.enquiryStageId)) {
        map.set(s.enquiryStageId, s);
      }
    });
  });
  if (map.size === 0) return [];
  return [...map.values()]
    .sort((a, b) => (a.sequenceNumber ?? 999) - (b.sequenceNumber ?? 999))
    .map((s) => ({
      id: s.stageName,
      label: s.stageName,
      stageType: s.stageType,
    }));
};

const buildOperationSummaryStages = (summary) => {
  const list = summary || [];
  if (list.length === 0) return [];

  // New flat shape: [{ operationStageId, operationStageName, totalCount }]
  if (list[0]?.operationStageId !== undefined) {
    return list.map((stage) => ({
      id: String(stage.operationStageId),
      label: stage.operationStageName,
      operationStageId: stage.operationStageId,
      totalOperations: stage.totalCount,
    }));
  }

  // Legacy nested shape: [{ categoryName, stages: [{ stageId, stageName, totalOperations }] }]
  return list.flatMap((category) =>
    (category.stages || []).map((stage) => ({
      id: String(stage.stageId),
      label: stage.stageName,
      operationStageId: stage.stageId,
      totalOperations: stage.totalOperations,
      categoryName: category.categoryName,
    })),
  );
};

const buildOperationStageColumns = (rawData) => {
  const map = new Map(); // operationStageId → stage object
  rawData.forEach((eq) => {
    (eq.operationStages || []).forEach((s) => {
      if (s.operationStageId && !map.has(s.operationStageId)) {
        map.set(s.operationStageId, s);
      }
    });
  });
  if (map.size === 0) return [];
  return [...map.values()]
    .sort((a, b) => (a.sequenceNumber ?? 999) - (b.sequenceNumber ?? 999))
    .map((s) => ({ id: s.name, label: s.name }));
};

// ─── Card placement logic ──────────────────────────────────────────────────────
//
// ENQUIRIES: find the stageHistory entry that is currently "Processing" (active).
// That entry's stageName is the column id.
//
// OPERATIONS: walk purchaseOrders → products → operationStages, find the furthest-
// reached op stage (last one that is active or completed). That stage's
// operationStageName is the column id.

const deriveEnquiryStageName = (eq) => {
  const sh = eq.stageHistories || [];
  if (sh.length === 0) return null;

  // Active (Processing/Assigned) stage wins
  const active = sh.find((s) => isActiveStatus(s.enquiryStatus));
  if (active) return active.stageName || null;

  // All completed → last stage
  const last = sh[sh.length - 1];
  return last?.stageName || null;
};

const deriveOperationStageName = (eq) => {
  const allOpStages = (eq.purchaseOrders || []).flatMap((o) =>
    (o.products || []).flatMap((p) => p.operationStages || []),
  );
  if (allOpStages.length === 0) return null;

  // Furthest reached = last stage that is active or completed (reverse search)
  const reached = [...allOpStages]
    .reverse()
    .find((s) => isActiveStatus(s.status) || isCompletedStatus(s.status));

  if (reached) return reached.operationStageName || null;

  // Nothing started yet → first stage
  return allOpStages[0]?.operationStageName || null;
};

// ─── Card shapers ──────────────────────────────────────────────────────────────

const toEnquiryCard = (eq, fallbackStageName = null) => {
  const company = eq.client?.companyName || eq.lead?.companyName || "Unknown";
  const contact = eq.client?.contactPerson || eq.lead?.contactPerson || null;
  const amount = eq.totalQuotationAmount || eq.totalEstimateAmount || 0;
  const activeStage =
    (eq.stageHistories || []).find((s) => isActiveStatus(s.enquiryStatus)) ||
    (eq.stageHistories || []).slice(-1)[0] ||
    null;

  const stageId = deriveEnquiryStageName(eq) || fallbackStageName;
  if (!stageId) return null;

  return {
    id: eq.enquiryCode || `ENQ-${eq.enquiryId}`,
    enquiryId: eq.enquiryId,
    name: company,
    contact,
    value: amount ? fmtMoney(amount) : null,
    totalQuotationAmount: eq.totalQuotationAmount || 0,
    status: eq.status,
    type: eq.type,
    priority: eq.priority,
    stageName:
      fallbackStageName ||
      activeStage?.stageName ||
      eq.currentStageName ||
      null,
    assignedTo: activeStage?.assignedTo || null,
    expectedClose: eq.expectedCloseDate || null,
    notes: eq.notes || null,
    stageId,
    _raw: eq,
  };
};

const toOperationCard = (eq, fallbackStageName = null) => {
  const allOpStages = (eq.purchaseOrders || []).flatMap((o) =>
    (o.products || []).flatMap((p) => p.operationStages || []),
  );
  const activeOpStage =
    allOpStages.find((s) => isActiveStatus(s.status)) ||
    [...allOpStages].reverse().find((s) => isCompletedStatus(s.status)) ||
    null;

  // The real /operations/pagination payload doesn't include an
  // operationStages array on each product, so deriveOperationStageName
  // will usually come back null. That's fine — this card was fetched
  // using a specific operationStageId, so the caller already knows which
  // column it belongs to. Fall back to that instead of dropping the card.
  const stageId = deriveOperationStageName(eq) || fallbackStageName;
  if (!stageId) return null;

  const company = eq.client?.companyName || eq.lead?.companyName || "Unknown";
  const activeOrder =
    (eq.purchaseOrders || []).find((o) => isActiveStatus(o.orderStatus)) ||
    (eq.purchaseOrders || [])[0] ||
    null;
  const amount = activeOrder?.totalAmount || 0;

  return {
    id: `OP-${eq.operationId}`,
    operationNo: eq.operationNo || null,
    enquiryId: eq.enquiryId,
    name: company,
    contact: eq.client?.contactPerson || eq.lead?.contactPerson || null,
    value: amount ? fmtMoney(amount) : null,
    orderNumber: activeOrder?.orderNumber || null,
    opStageName: activeOpStage?.operationStageName || fallbackStageName || null,
    assignedTo: activeOpStage?.assignedEmployeeName || null,
    stageId,
    notes: eq.notes || null,
    _raw: eq,
  };
};

// ─── Column colour palette ─────────────────────────────────────────────────────

const COL_PALETTE = [
  { bg: "#e6f1fb", color: "#0c447c" },
  { bg: "#eeedfe", color: "#3c3489" },
  { bg: "#faeeda", color: "#633806" },
  { bg: "#e1f5ee", color: "#085041" },
  { bg: "#eaf3de", color: "#27500a" },
  { bg: "#faece7", color: "#712b13" },
  { bg: "#fce7f6", color: "#6b1256" },
  { bg: "#f0f4ff", color: "#1e3a8a" },
];

// Enquiry stage → fixed colour by stageType
const ENQ_STAGE_COLOURS = {
  initial: {
    bg: "#e6f1fb",
    color: "#0c447c",
    border: "#60a5fa",
  },

  quotation: {
    bg: "#eeedfe",
    color: "#3c3489",
    border: "#8b5cf6",
  },

  negotiation: {
    bg: "#faeeda",
    color: "#633806",
    border: "#f59e0b",
  },

  won: {
    bg: "#eaf3de",
    color: "#27500a",
    border: "#22c55e",
  },

  lost: {
    bg: "#faece7",
    color: "#712b13",
    border: "#ef4444",
  },

  others: {
    bg: "#f3f4f6",
    color: "#4b5563",
    border: "#9ca3af",
  },
};

const getEnquiryStageColour = (stageType) => {
  const key = (stageType || "").toLowerCase();
  return ENQ_STAGE_COLOURS[key] || ENQ_STAGE_COLOURS.others;
};

function StatusBadge({ status }) {
  if (!status) return null;
  const s = normalizeStatus(status);
  const cls = isCompletedStatus(s)
    ? "kb__badge kb__badge--green"
    : isActiveStatus(s)
      ? "kb__badge kb__badge--blue"
      : isCancelledStatus(s)
        ? "kb__badge kb__badge--red"
        : "kb__badge kb__badge--yellow";
  return <span className={cls}>{capitalize(status)}</span>;
}

const NotesTooltip = ({ notes }) => {
  const [coords, setCoords] = useState(null);
  const iconRef = useRef(null);

  if (!notes) return null;

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    }
  };

  return (
    <span
      ref={iconRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setCoords(null)}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#fef9c3",
          border: "1px solid #fde047",
          cursor: "default",
          flexShrink: 0,
        }}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ca8a04"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="13" y2="17" />
        </svg>
      </span>

      {coords && (
        <span
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            transform: "translateX(-50%)",
            background: "#1e1b0e",
            color: "#fef3c7",
            fontSize: "0.75rem",
            lineHeight: 1.5,
            padding: "7px 10px",
            borderRadius: 8,
            whiteSpace: "pre-wrap",
            maxWidth: 260,
            width: "max-content",
            zIndex: 99999,
            boxShadow: "0 4px 16px rgba(0,0,0,0.28)",
            pointerEvents: "none",
          }}
        >
          {notes}
          <span
            style={{
              position: "absolute",
              bottom: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              borderWidth: "0 5px 5px",
              borderStyle: "solid",
              borderColor: "transparent transparent #1e1b0e",
              display: "block",
              width: 0,
              height: 0,
            }}
          />
        </span>
      )}
    </span>
  );
};

function KanbanCard({ card, mode }) {
  const isOps = mode === "operations";
  return (
    <div
      className={`kb__card priority-${(card.priority || "LOW").toLowerCase()}`}
    >
      <div className="kb__card-title">
        <div className="kb__card-name">{card.name}</div>
        <span className="kb__card-id">
          #{isOps ? card.operationNo || card.id : card.id}
        </span>
      </div>

      {card.contact && <div className="kb__card-contact">{card.contact}</div>}

      <div className="kb__card-footer">
        {mode === "enquiries" && card.value && (
          <span className="kb__card-value">{card.value}</span>
        )}

        {!isOps && card.status && <StatusBadge status={card.status} />}

        {card.expectedClose && (
          <span className="eqr-date">
            {new Date(card.expectedClose).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
            })}
          </span>
        )}

        {isOps && card.orderNumber && (
          <span className="kb__card-order">{card.orderNumber}</span>
        )}

        <NotesTooltip notes={card.notes} />
      </div>

      {card.assignedTo && (
        <div className="kb__card-assigned">👤 {card.assignedTo}</div>
      )}
    </div>
  );
}

function KanbanColumn({
  stage,
  cards,
  mode,
  colourStyle,
  page,
  onLoadMore,
  summaryTotals,
  isRemote = false,
  remoteHasMore = false,
  remoteLoading = false,
}) {
  // Client-computed fallback, used when /stage-summary doesn't have an
  // entry for this stage (e.g. still loading, or a stage with zero
  // enquiries that the summary endpoint omits entirely).
  const clientTotalAmount = cards.reduce((sum, card) => {
    const amount =
      card.totalQuotationAmount ?? card._raw?.totalQuotationAmount ?? 0;

    return sum + Number(amount);
  }, 0);

  const displayCount = summaryTotals?.count ?? cards.length;
  const displayAmount = summaryTotals?.amount ?? clientTotalAmount;

  // page 1 → cards[0:10], page 2 → cards[0:20], etc. Only the newly-revealed
  // batch is "page N" — cards[(N-1)*10 : N*10] — everything up to and
  // including it is what's shown.
  const visibleCount = page * KANBAN_COLUMN_PAGE_SIZE;
  const visibleCards = isRemote ? cards : cards.slice(0, visibleCount);
  const hasMore = isRemote ? remoteHasMore : cards.length > visibleCards.length;
  const nextPage = page + 1;

  // Reveals the next chunk of this column's cards as soon as the last
  // loaded card (e.g. the 10th) scrolls into view, rather than waiting
  // for the scroll container to come within some pixel distance of its
  // absolute bottom. That pixel-math approach is fragile against column
  // height changes; watching the last card directly means "reaching the
  // 10th one" is exactly the trigger, regardless of container sizing.
  const bodyRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore) return;
    const root = bodyRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !remoteLoading) {
          onLoadMore(stage.id);
        }
      },
      { root, rootMargin: "0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, remoteLoading, onLoadMore, stage.id, visibleCards.length]);

  return (
    <div
      className="kb__col"
      style={{
        backgroundColor: colourStyle.bg,
        borderTop: `4px solid ${colourStyle.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <div
        className="kb__col-head"
        style={{
          backgroundColor: colourStyle.bg,
          color: colourStyle.color,
          borderBottom: `1px solid ${colourStyle.border}`,
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          padding: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            className="kb__col-title"
            style={{
              fontWeight: 700,
              fontSize: "0.95rem",
            }}
          >
            {stage.label}
          </span>

          <span
            className="kb__col-count"
            style={{
              background: "rgba(255,255,255,0.8)",
              borderRadius: "999px",
              minWidth: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            {displayCount}
          </span>
        </div>

        {mode === "enquiries" && (
          <div
            className="kb__col-amount"
            style={{
              fontSize: "0.9rem",
              fontWeight: 700,
              opacity: 0.9,
            }}
          >
            {fmtMoney(displayAmount)}
          </div>
        )}
      </div>

      <div className="kb__col-body" ref={bodyRef}>
        {cards.length === 0 ? (
          <div className="kb__col-empty">
            {remoteLoading ? "Loading enquiries…" : "—"}
          </div>
        ) : (
          <>
            {visibleCards.map((card) => (
              <KanbanCard key={card.id} card={card} mode={mode} />
            ))}
            {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
            {(hasMore || remoteLoading) && (
              <div className="kb__col-loadmore">
                {remoteLoading
                  ? "Loading more enquiries…"
                  : isRemote
                    ? "Scroll to load more"
                    : `Loading page ${nextPage}…`}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function KanbanBoard({
  axiosInstance,
  getApiEndpoint,
  data: externalData,
  loading: externalLoading,
  error: externalError,
  onRefresh,
  onTabChange,
  activeTab,
  stageSummary = [],
}) {
  const [internalData, setInternalData] = useState([]);
  const [internalStageSummary, setInternalStageSummary] = useState([]);
  const [internalLoading, setInternalLoading] = useState(!externalData);
  const [internalError, setInternalError] = useState(null);
  const [cardsByStage, setCardsByStage] = useState({});
  const [stagePaging, setStagePaging] = useState({});
  const [loadingStages, setLoadingStages] = useState(() => new Set());
  const [operationSummary, setOperationSummary] = useState([]);
  const [operationSummaryLoading, setOperationSummaryLoading] = useState(false);
  const [operationSummaryError, setOperationSummaryError] = useState(null);
  const stagePagingRef = useRef({});
  const loadingStagesRef = useRef(new Set());

  const isControlled = externalData !== undefined;
  const rawData = isControlled ? externalData : internalData;
  const loading = isControlled ? externalLoading : internalLoading;
  const error = isControlled ? externalError : internalError;

  const fetchData = useCallback(async () => {
    if (isControlled) {
      onRefresh?.();
      return;
    }
    try {
      setInternalLoading(true);
      setInternalError(null);
      const res = await axiosInstance.get(getApiEndpoint("/stage-summary"));
      const summary = res.data?.data || [];
      setInternalStageSummary(summary);
      setInternalData(stageSummaryToEnquiries(summary));
    } catch (err) {
      console.error("[KanbanBoard] fetch error:", err);
      setInternalError("Failed to load enquiries.");
    } finally {
      setInternalLoading(false);
    }
  }, [axiosInstance, getApiEndpoint, isControlled, onRefresh]);

  useEffect(() => {
    if (!isControlled) fetchData();
  }, [fetchData, isControlled]);

  // Per-stage-name enquiry count + quotation total, sourced from
  // /stage-summary rather than summed from whatever cards happen to be
  // loaded client-side (see buildStageTotalsFromSummary above).
  const currentStageSummary = isControlled
    ? stageSummary
    : internalStageSummary;
  const stageTotals = useMemo(
    () => buildStageTotalsFromSummary(currentStageSummary),
    [currentStageSummary],
  );

  const isEnquiries = activeTab === "enquiries";
  const operationStages = useMemo(
    () => buildOperationSummaryStages(operationSummary),
    [operationSummary],
  );

  useEffect(() => {
    if (isEnquiries) return;

    const fetchOperationSummary = async () => {
      try {
        setOperationSummaryLoading(true);
        setOperationSummaryError(null);
        const response = await axiosInstance.get(
          getApiEndpoint("/operations/operation-status-summary"),
        );
        setOperationSummary(response.data?.data || []);
      } catch (err) {
        console.error("[KanbanBoard] operation summary fetch error:", err);
        setOperationSummaryError("Failed to load operation stages.");
      } finally {
        setOperationSummaryLoading(false);
      }
    };

    fetchOperationSummary();
  }, [axiosInstance, getApiEndpoint, isEnquiries]);

  const remoteStages = useMemo(
    () =>
      isEnquiries
        ? currentStageSummary.map((stage) => ({
            ...stage,
            id: stage.stageName,
            label: stage.stageName,
            total: stage.totalEnquiries,
          }))
        : operationStages.map((stage) => ({
            ...stage,
            total: stage.totalOperations,
          })),
    [currentStageSummary, isEnquiries, operationStages],
  );
  // `externalData` is already filtered by the overview. Do not replace it
  // with a new unfiltered remote fetch, otherwise search/date/quick filters
  // appear to work in the list but not on Kanban cards.
  const useRemoteCards = !isControlled && remoteStages.length > 0;

  const loadStageCards = useCallback(
    async (stageId) => {
      const stage = remoteStages.find((item) => item.id === stageId);
      if (!stage || loadingStagesRef.current.has(stageId)) return;

      const paging = stagePagingRef.current[stageId] || {
        nextPage: 0,
        hasMore: true,
      };
      if (!paging.hasMore) return;

      loadingStagesRef.current.add(stageId);
      setLoadingStages(new Set(loadingStagesRef.current));
      let loaded = false;
      try {
        const response = await axiosInstance.get(
          getApiEndpoint(
            isEnquiries
              ? "/pagination/sales-enquiries"
              : "/operations/pagination",
          ),
          {
            params: {
              [isEnquiries ? "enquiryStageId" : "operationStageId"]: isEnquiries
                ? stage.enquiryStageId
                : stage.operationStageId,
              page: paging.nextPage,
              size: KANBAN_COLUMN_PAGE_SIZE,
            },
          },
        );
        const newCards = (response.data?.data || [])
          .map((record) =>
            isEnquiries
              ? toEnquiryCard(record, stage.label)
              : toOperationCard(record, stage.label),
          )
          .filter(Boolean)
          .map((card) => ({ ...card, stageId }));

        if (isEnquiries) {
          const summaryOrder = new Map(
            (stage.histories || []).map((history, index) => [
              String(history.enquiryId),
              index,
            ]),
          );
          newCards.sort(
            (a, b) =>
              (summaryOrder.get(String(a.enquiryId)) ??
                Number.MAX_SAFE_INTEGER) -
              (summaryOrder.get(String(b.enquiryId)) ??
                Number.MAX_SAFE_INTEGER),
          );
        }

        setCardsByStage((previous) => {
          const existing = previous[stageId] || [];
          const ids = new Set(existing.map((card) => card.id));
          const uniqueNewCards = newCards.filter((card) => {
            if (ids.has(card.id)) return false;
            ids.add(card.id);
            return true;
          });
          return {
            ...previous,
            [stageId]: [...existing, ...uniqueNewCards],
          };
        });
        loaded = true;
      } catch (err) {
        console.error(
          `[KanbanBoard] failed to load ${stage.label} cards:`,
          err,
        );
      } finally {
        if (loaded) {
          const nextPaging = {
            nextPage: paging.nextPage + 1,
            hasMore:
              (paging.nextPage + 1) * KANBAN_COLUMN_PAGE_SIZE <
              Number(stage.total || 0),
          };
          stagePagingRef.current = {
            ...stagePagingRef.current,
            [stageId]: nextPaging,
          };
          setStagePaging(stagePagingRef.current);
        }
        loadingStagesRef.current.delete(stageId);
        setLoadingStages(new Set(loadingStagesRef.current));
      }
    },
    [axiosInstance, getApiEndpoint, isEnquiries, remoteStages],
  );

  useEffect(() => {
    if (!useRemoteCards) return;
    stagePagingRef.current = {};
    loadingStagesRef.current = new Set();
    setCardsByStage({});
    setStagePaging({});
    setLoadingStages(new Set());
    remoteStages.forEach((stage) => loadStageCards(stage.id));
  }, [loadStageCards, remoteStages, useRemoteCards]);

  // ── Build columns from the stage config arrays on the enquiries ──────────────
  const stages =
    remoteStages.length > 0
      ? remoteStages
      : isEnquiries
        ? buildEnquiryStageColumns(rawData)
        : buildOperationStageColumns(rawData);

  // ── Build cards ──────────────────────────────────────────────────────────────
  const cards = useRemoteCards
    ? Object.values(cardsByStage).flat()
    : isEnquiries
      ? rawData.map(toEnquiryCard).filter(Boolean)
      : rawData
          .map((record) => {
            const card = toOperationCard(
              record,
              record.operationStageName,
            );
            return card
              ? { ...card, stageId: String(record.operationStageId) }
              : null;
          })
          .filter(Boolean);

  // Which page each column is on (page 1 = first 10 cards, page 2 = next
  // 10, …). Reset whenever the tab or the underlying data changes, since
  // columns/cards are rebuilt.
  const [pageByStage, setPageByStage] = useState({});
  useEffect(() => {
    setPageByStage({});
  }, [activeTab, rawData]);

  const handleLoadMoreColumn = useCallback((stageId) => {
    setPageByStage((prev) => ({
      ...prev,
      [stageId]: (prev[stageId] ?? 1) + 1,
    }));
  }, []);

  return (
    <div className="kb">
      {/* Board */}
      {loading || operationSummaryLoading ? (
        <LoadingScreen />
      ) : error || operationSummaryError ? (
        <div className="kb__state kb__state--error">
          <span>{error || operationSummaryError}</span>
          <button className="kb__retry-btn" onClick={fetchData}>
            Retry
          </button>
        </div>
      ) : stages.length === 0 ? (
        <div className="kb__state">
          <span>No stage configuration found in data.</span>
        </div>
      ) : (
        <div className="kb__board">
          {stages.map((stage, index) => {
            const colCards = useRemoteCards
              ? cardsByStage[stage.id] || []
              : cards.filter((c) => c.stageId === stage.id);
            const colourStyle = isEnquiries
              ? getEnquiryStageColour(stage.stageType)
              : COL_PALETTE[index % COL_PALETTE.length];
            return (
              <KanbanColumn
                key={stage.id}
                stage={stage}
                cards={colCards}
                mode={activeTab}
                colourStyle={colourStyle}
                page={pageByStage[stage.id] ?? 1}
                onLoadMore={
                  useRemoteCards ? loadStageCards : handleLoadMoreColumn
                }
                // Summary totals describe the unfiltered data set. With
                // controlled data, calculate totals from the filtered cards
                // so column counts and amounts react to active filters too.
                summaryTotals={
                  useRemoteCards
                    ? isEnquiries
                      ? stageTotals.get(stage.id)
                      : { count: stage.totalOperations }
                    : undefined
                }
                isRemote={useRemoteCards}
                remoteHasMore={useRemoteCards && stagePaging[stage.id]?.hasMore}
                remoteLoading={useRemoteCards && loadingStages.has(stage.id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
